import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastService } from '../../../services/notification/toast.service';
import { WebService } from '../../../services/web/web.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Validators, FormGroup, FormControl } from '@angular/forms';
import { Location } from "@angular/common";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlanType } from "../../../shared/enums/plan-type";

@Component({
    selector: 'app-update-plan',
    templateUrl: './update-plan.component.html',
    styleUrls: ['./update-plan.component.scss'],
    standalone: false
})
export class UpdatePlanComponent implements OnInit, OnDestroy {

  plans: any = [];

  readonly PlanType = PlanType;

  form: FormGroup;

  isVariable: boolean = false;
  isOnDemand: boolean = false;
  private savedFieldValues: {[key: string]: any} = {};

  // BILL-2582: bloqueo de cambio de tipo NORMAL/LINK_DE_PAGO -> VARIABLE cuando ya hay clientes
  // suscritos. El flag has_suscriptions del GET /plan/:id ya cuenta cualquier estatus.
  hasSubscriptions: boolean = false;
  currentType: number = PlanType.NORMAL;

  private destroy$ = new Subject<void>();

  @ViewChild('inputFile') myInputVariable: ElementRef | undefined;

  frecuencies: any;
  imageName = null;
  image64: any;
  imageTemp: any;
  imageType: any;
  onboardingConfigID: any;
  planID: any;
  frecuencyName: string = 'Mensuales';

  constructor(
    private location: Location,
    private activateRoute: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private spinner: SpinnerService,
    private webService: WebService
  ) {
    this.form = new FormGroup({
      id: new FormControl(''),
      name_plan: new FormControl('', Validators.required),
      feeCost_plan: new FormControl('', Validators.required),
      freezeCostPlan: new FormControl('', Validators.required),
      enrollmentCost_plan: new FormControl(0),
      frecuency: new FormControl('', Validators.required),
      status_plan: new FormControl('', Validators.required),
      taxRate_plan: new FormControl('', Validators.required),
      service: new FormControl(''),
      description: new FormControl('', Validators.required),
      idPlan_partner: new FormControl("123", Validators.required),
      cobro_by_partner: new FormControl(false, Validators.required),
      verify_invoices_on_partner: new FormControl(false, Validators.required),
      charge_inmediately: new FormControl(false, Validators.required),
      qty_installments_to_collect_plan: new FormControl(''),
      is_visible_plan: new FormControl(false, Validators.required),
      type: new FormControl(PlanType.NORMAL, Validators.required)
    });
  }

  // BILL-2582: bloquea VARIABLE en el select cuando el plan ya tiene suscriptores y aún no es VARIABLE.
  get isVariableBlockedBySubscribers(): boolean {
    return this.hasSubscriptions && this.currentType !== PlanType.VARIABLE;
  }

  ngOnInit(): void {
    this.form.controls['type'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.onTypeChange(+type));

    this.spinner.open();
    this.webService.get(this.webService.HOST + "/frecuency").subscribe(response => {
      this.frecuencies = response.result;
      this.activateRoute.params.subscribe(params => {
        this.onboardingConfigID = params['id'];
        this.planID = +params['plan'];
        this.webService.get(this.webService.HOST + "/plan/" + this.planID + "?onboardingConfig=" + this.onboardingConfigID).subscribe(response => {
          const plan = response.result;
          this.hasSubscriptions = response.has_suscriptions === true;
          this.currentType = plan.type ?? PlanType.NORMAL;
          this.form.patchValue({
            id: plan.id,
            name_plan: plan.name_plan,
            description: plan.description,
            frecuency: plan.frecuency?.id ?? '',
            status_plan: plan.status_plan,
            feeCost_plan: plan.feeCost_plan,
            freezeCostPlan: plan.freezeCostPlan,
            enrollmentCost_plan: plan.enrollmentCost_plan,
            taxRate_plan: plan.taxRate_plan,
            service: { id: plan.service?.id },
            cobro_by_partner: plan.cobro_by_partner,
            verify_invoices_on_partner: plan.verify_invoices_on_partner,
            charge_inmediately: plan.charge_inmediately,
            qty_installments_to_collect_plan: plan.qty_installments_to_collect_plan === null ? 0 : plan.qty_installments_to_collect_plan,
            is_visible_plan: plan.is_visible_plan,
            type: plan.type ?? PlanType.NORMAL
          }, { emitEvent: false });
          this.imageTemp = plan.url_image;
          this.frecuencyName = plan.frecuency?.name_frecuency ?? this.frecuencyName;
          this.configureTypeControls(+this.form.controls['type'].value, true);
          if (this.hasSubscriptions) {
            this.form.controls['qty_installments_to_collect_plan'].disable({ emitEvent: false });
          }
          if (this.isOnDemand) {
            this.form.controls['type'].disable({ emitEvent: false });
          }
          this.spinner.close();
        }, err => {
          this.spinner.close();
          this.toast.showError(err);
          setTimeout(() => {
            this.location.back();
          }, 2000);
        });
      }, err => {
        this.toast.showError(err);
        this.spinner.close();
      });
    }, err => {
      this.spinner.close();
      this.toast.showError(err);
    });
  }

  send() {
    if (!this.form.valid) {
      return this.toast.showError("Por favor, complete el formulario...");
    }

    // getRawValue incluye controles disabled (type bloqueado para ON_DEMAND)
    const payload = this.form.getRawValue();
    payload.frecuency = { id: +payload.frecuency };
    payload.image64 = this.image64;
    payload.imageName = this.imageName;
    payload.imageType = this.imageType;
    payload.onboarding_config = { id: +this.onboardingConfigID };
    payload.type = +payload.type;

    this.spinner.open();
    this.webService.put(
      { plan: payload, onboarding: true },
      this.webService.HOST + "/partner/" + this.planID + "/plan/" + this.planID
    ).subscribe(
      response => {
        this.spinner.close();
        this.toast.showSuccess("Plan actualizado correctamente...");
        this.location.back();
      },
      err => {
        this.spinner.close();
        if (err?.error?.code === 'BILL_PLAN_TYPE_CHANGE_BLOCKED_BY_SUBSCRIBERS') {
          this.toast.showError(err.error.message);
          return;
        }
        this.toast.showError(err);
      }
    );
  }

  onLogoChange(event: any) {
    const reader = new FileReader();
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.imageName = file.name;
        this.imageType = file.type;
        this.image64 = reader.result!.toString();
      };
    }
  }

  goBack() {
    this.router.navigate(["../.."], { relativeTo: this.activateRoute, queryParamsHandling: "preserve" });
  }

  onChangeFrecuency(index: number) {
    index = index - 1;
    this.frecuencyName = this.frecuencies[index].name_frecuency;
  }

  handleCreatePlanClick() {
    this.router.navigate(["/create"], { relativeTo: this.activateRoute, queryParamsHandling: "preserve" });
  }

  private onTypeChange(type: number): void {
    this.configureTypeControls(type, false);
  }

  private configureTypeControls(type: number, preserveValues: boolean): void {
    if (type === PlanType.VARIABLE) {
      this.isVariable = true;
      this.isOnDemand = false;
      if (!preserveValues) {
        this.saveFieldValues(['feeCost_plan', 'taxRate_plan', 'freezeCostPlan', 'is_visible_plan']);
      }
      this.applyVariableDefaults(preserveValues);
    } else if (type === PlanType.ON_DEMAND) {
      this.isVariable = false;
      this.isOnDemand = true;
      if (!preserveValues) {
        this.saveFieldValues();
      }
      this.applyOnDemandDefaults(preserveValues);
    } else {
      this.isVariable = false;
      this.isOnDemand = false;
      this.restoreNonVariableValidators(preserveValues);
    }
  }

  private applyVariableDefaults(preserveValues = false): void {
    const hiddenControls = ['feeCost_plan', 'taxRate_plan', 'freezeCostPlan', 'is_visible_plan'];
    hiddenControls.forEach((name) => {
      const control = this.form.controls[name];
      control.enable({ emitEvent: false });
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
    });

    const frecuencyControl = this.form.controls['frecuency'];
    frecuencyControl.enable({ emitEvent: false });
    frecuencyControl.setValidators([Validators.required]);
    frecuencyControl.updateValueAndValidity({ emitEvent: false });

    this.form.controls['enrollmentCost_plan'].enable({ emitEvent: false });

    if (preserveValues) {
      return;
    }

    this.form.controls['feeCost_plan'].setValue(0, { emitEvent: false });
    this.form.controls['taxRate_plan'].setValue(0, { emitEvent: false });
    this.form.controls['freezeCostPlan'].setValue(0, { emitEvent: false });
    this.form.controls['is_visible_plan'].setValue(false, { emitEvent: false });

    this.imageName = null;
    this.image64 = null;
    this.imageType = null;
    if (this.myInputVariable) {
      this.myInputVariable.nativeElement.value = '';
    }
  }

  private saveFieldValues(fields = ['feeCost_plan', 'taxRate_plan', 'freezeCostPlan', 'enrollmentCost_plan', 'is_visible_plan', 'frecuency']): void {
    fields.forEach(name => {
      this.savedFieldValues[name] = this.form.controls[name].value;
    });
  }

  private applyOnDemandDefaults(preserveValues = false): void {
    const fixedControls = ['feeCost_plan', 'taxRate_plan', 'freezeCostPlan', 'is_visible_plan'];
    fixedControls.forEach(name => {
      const control = this.form.controls[name];
      control.clearValidators();
      control.updateValueAndValidity({ emitEvent: false });
      control.disable({ emitEvent: false });
    });

    if (preserveValues) {
      return;
    }

    this.form.controls['qty_installments_to_collect_plan'].setValue('', { emitEvent: false });
    this.imageName = null;
    this.image64 = null;
    this.imageType = null;
    if (this.myInputVariable) {
      this.myInputVariable.nativeElement.value = '';
    }
  }

  private restoreNonVariableValidators(preserveValues = false): void {
    const requiredControls = ['feeCost_plan', 'taxRate_plan', 'freezeCostPlan', 'is_visible_plan', 'frecuency'];
    requiredControls.forEach((name) => {
      const control = this.form.controls[name];
      control.enable({ emitEvent: false });
      if (!preserveValues && this.hasSavedFieldValue(name)) {
        const saved = this.savedFieldValues[name];
        const hasValid = saved !== undefined && saved !== null && saved !== '' && saved !== 0.01 && saved !== false;
        control.setValue(hasValid ? saved : '', { emitEvent: false });
      }
      control.setValidators([Validators.required]);
      control.updateValueAndValidity({ emitEvent: false });
    });
    this.form.controls['enrollmentCost_plan'].enable({ emitEvent: false });
    if (!preserveValues && this.hasSavedFieldValue('enrollmentCost_plan')) {
      const savedEnroll = this.savedFieldValues['enrollmentCost_plan'];
      const hasValidEnroll = savedEnroll !== undefined && savedEnroll !== null && savedEnroll !== '' && savedEnroll !== 0.01;
      this.form.controls['enrollmentCost_plan'].setValue(hasValidEnroll ? savedEnroll : 0, { emitEvent: false });
    }
    this.savedFieldValues = {};
  }

  private hasSavedFieldValue(name: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.savedFieldValues, name);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
